/**
 * Renders AI-generated UI specs in Herald chat.
 * Component registry lives in {@link heraldRendererRegistry}; this file adds chat-only wrappers.
 */
import type {
  ComponentRegistry,
  ComponentRenderer,
  ComponentRenderProps,
} from "@json-render/react";
import {
  ActionProvider,
  Renderer,
  StateProvider,
  useStateStore,
  VisibilityProvider,
} from "@json-render/react";
import { type ReactNode, useMemo } from "react";
import {
  HeraldCatalogCardSubtree,
  useHeraldInsideCatalogCard,
} from "@/components/ui/chat/table/ChatTable";
import { ReportThemeProvider } from "@/components/ui/report/ReportThemeProvider";
import {
  HERALD_CATALOG_COMPONENT_DATA_ATTR,
  HERALD_CATALOG_UNKNOWN_DATA_ATTR,
  HERALD_UI_DATA_ATTR,
  HERALD_UI_VALUE_CATALOG,
} from "@/lib/chat/heraldChatDomDataAttrs";
import { useHeraldChatPngExportSuppressed } from "@/lib/chat/heraldChatPngExportSuppressedContext";
import {
  createHeraldRendererHandlers,
  heraldRendererRegistry,
} from "@/lib/chat/heraldRendererRegistry";
import { migrateLegacyHeraldSpec } from "@/lib/chat-sanitize/migrateLegacyHeraldSpec";
import { sanitizeSpecRefs } from "@/lib/chat-sanitize/sanitizeSpecRefs";
import { prepareReportSpecForRender } from "@/lib/report/prepareReportSpecForRender";
import { reportRendererRegistry } from "@/lib/template-studio/reportRendererRegistry";
import { useHeraldSpecDevTools } from "./dev-tools/useHeraldSpecDevTools";
import { HeraldCatalogExportFrame } from "./heraldCatalogExportFrame";
import { getHeraldComponentExportMode } from "./heraldExportableComponents";

interface HeraldCatalogRegistryWrapperProps {
  readonly componentType: string;
  readonly RendererComp: ComponentRenderer;
  readonly catalogProps: ComponentRenderProps;
}

const HeraldCatalogRegistryWrapper = ({
  componentType,
  RendererComp,
  catalogProps,
}: HeraldCatalogRegistryWrapperProps) => {
  const insideCatalogCard = useHeraldInsideCatalogCard();
  const exportMode = getHeraldComponentExportMode(componentType);
  const pngExportSuppressed = useHeraldChatPngExportSuppressed();
  const effectiveExportMode =
    componentType === "Table" && insideCatalogCard
      ? null
      : exportMode === "png" && pngExportSuppressed
        ? null
        : exportMode;
  const rendered = <RendererComp {...catalogProps} />;
  const withCardSubtree =
    componentType === "Card" ? (
      <HeraldCatalogCardSubtree>{rendered}</HeraldCatalogCardSubtree>
    ) : (
      rendered
    );
  return (
    <div
      {...{ [HERALD_CATALOG_COMPONENT_DATA_ATTR]: componentType }}
      {...{ [HERALD_UI_DATA_ATTR]: HERALD_UI_VALUE_CATALOG }}
      style={{ display: "contents" }}
    >
      {effectiveExportMode === null ? (
        withCardSubtree
      ) : (
        <HeraldCatalogExportFrame
          componentType={componentType}
          mode={effectiveExportMode}
        >
          {withCardSubtree}
        </HeraldCatalogExportFrame>
      )}
    </div>
  );
};

const wrapRegistryWithCatalogMarkers = (
  registry: ComponentRegistry,
): ComponentRegistry => {
  const wrapped: ComponentRegistry = {};
  for (const [componentType, Component] of Object.entries(registry)) {
    const RendererComp = Component as ComponentRenderer;
    const RegistryRow = (catalogProps: ComponentRenderProps) => (
      <HeraldCatalogRegistryWrapper
        componentType={componentType}
        RendererComp={RendererComp}
        catalogProps={catalogProps}
      />
    );
    RegistryRow.displayName = `HeraldCatalog(${componentType})`;
    wrapped[componentType] = RegistryRow;
  }
  return wrapped;
};

const registryWithMarkers = wrapRegistryWithCatalogMarkers({
  ...heraldRendererRegistry,
  ...reportRendererRegistry,
});

/** Fallback when the spec references a component not in the registry. */
const UnknownComponentFallback = ({ element }: ComponentRenderProps) => (
  <div
    {...{ [HERALD_CATALOG_COMPONENT_DATA_ATTR]: element.type }}
    {...{ [HERALD_CATALOG_UNKNOWN_DATA_ATTR]: "" }}
    {...{ [HERALD_UI_DATA_ATTR]: HERALD_UI_VALUE_CATALOG }}
    className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
    role="alert"
  >
    Unknown or unsupported component: <strong>{element.type}</strong>. This
    component is not available in Herald.
  </div>
);

interface HeraldSpecRendererProps {
  spec: Parameters<typeof Renderer>[0]["spec"];
  fallback?: Parameters<typeof Renderer>[0]["fallback"];
  reportDateValue?: string;
}

/** Wires action handlers from registry (must be inside StateProvider). */
const HeraldActionProvider = ({ children }: { children: ReactNode }) => {
  const store = useStateStore();
  const handlers = useMemo(() => {
    const setState = (
      updater: (prev: Record<string, unknown>) => Record<string, unknown>,
    ) => {
      store.update(updater(store.getSnapshot() ?? {}));
    };
    return createHeraldRendererHandlers(
      () => setState,
      () => store.getSnapshot() ?? {},
    );
  }, [store]);
  return <ActionProvider handlers={handlers}>{children}</ActionProvider>;
};

/** Renderer for Herald chat spec segments. Use with spec and fallback. */
export const HeraldSpecRenderer = ({
  spec,
  fallback,
  reportDateValue,
}: HeraldSpecRendererProps) => {
  const sanitized = useMemo(() => sanitizeSpecRefs(spec), [spec]);
  const migrated = useMemo(
    () =>
      prepareReportSpecForRender(
        migrateLegacyHeraldSpec(sanitized),
        reportDateValue,
      ),
    [reportDateValue, sanitized],
  );

  useHeraldSpecDevTools({
    raw: spec,
    sanitized,
    rendered: migrated,
  });

  if (!migrated?.root || !migrated?.elements) {
    return null;
  }

  const hasReportComponent = Object.values(migrated.elements).some((element) =>
    element.type.startsWith("Report"),
  );
  const renderer = (
    <div
      className={
        "w-full text-sm " +
        "**:data-[slot=card]:text-sm! **:data-[slot=card-content]:text-sm! " +
        "**:data-[slot=card-title]:text-base! **:data-[slot=card-description]:text-sm!"
      }
    >
      <Renderer
        spec={migrated}
        registry={registryWithMarkers}
        fallback={fallback ?? UnknownComponentFallback}
      />
    </div>
  );

  return (
    <StateProvider initialState={migrated.state ?? {}}>
      <HeraldActionProvider>
        <VisibilityProvider>
          {hasReportComponent ? (
            <ReportThemeProvider className="w-full">
              {renderer}
            </ReportThemeProvider>
          ) : (
            renderer
          )}
        </VisibilityProvider>
      </HeraldActionProvider>
    </StateProvider>
  );
};

export { UnknownComponentFallback };
